import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { LongLogoBlack } from '@images';

import { Icon, SidebarItem } from '@components';
import { FEATURE_FLAGS } from '@constants/feature';
import { AccountType, OrgStatus } from '@constants/generic';
import { LEAD_MANAGEMENT_ROLES } from '@constants/role';
import { DASHBOARD, GROUPS } from '@constants/routes';
import {
  INDIVIDUAL_MENU_ITEMS,
  INDIVIDUAL_PROFILE_ITEMS,
  ORG_MENU_ITEMS,
  ORG_PROFILE_SECTIONS,
  PROFILE_ITEM_PATHS
} from '@constants/sideMenu';
import useCheckPermission from '@hooks/checkPermission';
import useOnClickOutside from '@utils/hooks';
import { Org } from 'types/organization';

import AccountModals from './AccountModals';
import ProfileOptions from './ProfileOptions';

interface SideNavBarProps {
  orgList: Org[];
  subscriptionData: any;
}

const SideNavBar = (props: SideNavBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isProfileList = PROFILE_ITEM_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );

  const { orgList, subscriptionData } = props;

  const ref = useRef(null);
  const { currentOrg, accountType } = useSelector((state: any) => state.user);
  const { rolesAndPermissions } = useSelector((state: any) => state.user);
  const { orgFeatureFlags } = useSelector((state: any) => state.user);
  const { currentBranchFilter } = useSelector((state: any) => state.branch);

  const [accountMethods, setAccountMethods] = useState<any>(null);

  useOnClickOutside(ref, (e) => {
    e.stopPropagation();
  });

  const { checkPermission } = useCheckPermission();

  const showSideNavBarBackButton = !LEAD_MANAGEMENT_ROLES.includes(
    currentOrg?.role
  );

  const orgMenuItems = isProfileList ? ORG_PROFILE_SECTIONS : ORG_MENU_ITEMS;
  const individualMenuItems = isProfileList
    ? INDIVIDUAL_PROFILE_ITEMS
    : INDIVIDUAL_MENU_ITEMS;

  const menuItems =
    accountType.type === AccountType.ORGANIZATION
      ? orgMenuItems
      : individualMenuItems;

  const permittedSideNavItems = (menuItems as any)?.filter(
    (navItem: any) =>
      (!navItem?.permissions ||
        navItem?.permissions.some((permission: any) =>
          checkPermission([permission])
        )) &&
      (!navItem?.featureFlag || orgFeatureFlags?.includes(navItem?.featureFlag))
  );

  const isExcludedSideNavItem = (id: string) => {
    if (id === 'subscriptions')
      return (
        currentOrg?.status === OrgStatus.VERIFICATION_PENDING ||
        currentOrg?.status === OrgStatus.VERIFICATION_SUBMITTED
      );

    if (id === 'event-registration')
      return !(
        orgFeatureFlags && orgFeatureFlags?.includes(FEATURE_FLAGS.EVENTS)
      );

    if (id === 'docs')
      return !(
        orgFeatureFlags &&
        orgFeatureFlags.includes(FEATURE_FLAGS.API_INTEGRATIONS)
      );

    return false;
  };

  // Helper to filter and exclude items in a section
  const getPermittedSectionItems = (section: any[]) =>
    section.filter(
      (navItem: any) =>
        (!navItem?.permissions ||
          navItem?.permissions.some((permission: any) =>
            checkPermission([permission])
          )) &&
        // eslint-disable-next-line max-len
        (!navItem?.featureFlag ||
          orgFeatureFlags?.includes(navItem?.featureFlag)) &&
        !isExcludedSideNavItem(navItem.id)
    );

  const renderProfileListWithSections = () =>
    ORG_PROFILE_SECTIONS.map((section) => {
      const permittedItems = getPermittedSectionItems(section);
      if (permittedItems.length === 0) return null;

      const sectionKey =
        permittedItems[0]?.id ||
        JSON.stringify(permittedItems.map((i) => i.id));
      const sectionIdx = ORG_PROFILE_SECTIONS.indexOf(section);

      // Check if next section has any permitted items
      let showDivider = false;
      if (sectionIdx < ORG_PROFILE_SECTIONS.length - 1) {
        const nextSection = ORG_PROFILE_SECTIONS[sectionIdx + 1];
        const nextPermitted = getPermittedSectionItems(nextSection);
        showDivider = nextPermitted.length > 0;
      }

      return (
        <div key={sectionKey}>
          {permittedItems.map((item) => (
            <SidebarItem
              item={item}
              path={item.route}
              isOrgLevelItem
              key={item.id}
              isProfileList={isProfileList}
            />
          ))}
          {showDivider && (
            <div className="my-2 h-px w-full bg-darkGrey opacity-20" />
          )}
        </div>
      );
    });

  const backNavigation = () => {
    navigate(
      accountType.type === AccountType.ORGANIZATION ? DASHBOARD : GROUPS.HOME
    );
  };

  const renderSidebarElement = () =>
    permittedSideNavItems.map((item: any) => {
      if (isExcludedSideNavItem(item.id)) return undefined;
      const { id, route } = item;
      let path = route;

      if (id === 'groups') {
        path = GROUPS.HOME;
      }

      return (
        <SidebarItem
          item={item}
          path={path}
          key={id}
          branchId={currentBranchFilter?.branchId}
          isProfileList={isProfileList}
        />
      );
    });

  const isSandBoxEnv = process.env.REACT_APP_ENVIRONMENT === 'sandbox';

  return (
    <>
      {!rolesAndPermissions?.permissions?.length ? (
        <div className="group absolute left-0 top-0 z-30 flex h-full w-22 flex-col justify-between bg-theme shadow-sm transition-all duration-300 ease-in-out" />
      ) : (
        <div className="no-scrollbar group absolute left-0 top-0 z-30 flex h-full w-56 flex-col justify-between overflow-auto bg-theme px-2 shadow-sm transition-all duration-300 ease-in-out">
          <div className="flex h-full flex-col justify-between ">
            <div
              className={`flex flex-col py-3 ${
                isSandBoxEnv ? 'mt-32' : 'mt-18'
              } ${isProfileList ? '' : 'gap-3'}`}
            >
              {isProfileList ? (
                <>
                  {showSideNavBarBackButton && (
                    <Icon
                      className="cursor-pointer"
                      name="back"
                      onClick={backNavigation}
                    />
                  )}
                  <div className="mt-2.5">
                    {renderProfileListWithSections()}
                  </div>
                  <div className="px-1">
                    <ProfileOptions
                      orgList={orgList}
                      accountMethods={accountMethods}
                      subscriptionData={subscriptionData}
                      labelStyle="font-medium text-bgGrey"
                      isProfileList={isProfileList}
                    />
                  </div>
                </>
              ) : (
                renderSidebarElement()
              )}
            </div>

            <div className="flex items-center gap-2 px-6 py-4">
              <LongLogoBlack className="h-8 w-auto" />
            </div>
          </div>
        </div>
      )}
      <AccountModals orgList={orgList} setAccountMethods={setAccountMethods} />
    </>
  );
};

export default SideNavBar;
